import React, { useEffect, useMemo } from 'react';

/* Translations */
import { useTranslation } from 'react-i18next';

/* Components */
import PermissionsNoToggle from './PermissionsNoToggle';
import PermissionsWithToggle from './PermissionsWithToggle';
import RendererIf from 'components/RendererIf';

/* Contexts, Hooks, Services */
import usePermission from 'hooks/usePermission';
import useCurrentUser from 'hooks/useCurrentUser';

/* Helpers */
import { idsArray } from '../../helpers';
import { ActionPermission } from 'lib/specialPermissions';

interface SpecialPermissionsProps {
  teacherId: string;
  teacherPermissions: any;
  enableButton: (data: any) => void;
  editedPermissions: any;
  handlePermissionChange: (data: any) => void;
}

const SpecialPermissions = ({
  teacherId,
  teacherPermissions,
  enableButton,
  editedPermissions,
  handlePermissionChange,
}: SpecialPermissionsProps) => {
  const { i18n } = useTranslation();

  /* Permissions Logic */
  const { fetchPermissions } = usePermission();
  const { can, user } = useCurrentUser();

  /* user can only edit if he is not viewing his own profile and has permission to edit */
  const hasPermission = can(
    ActionPermission.GIVE_SPECIAL_PERMISSIONS_TO_TEACHERS,
  );
  const isUserProfile = teacherId === user.id.toString();
  const canEdit = !isUserProfile && hasPermission;

  useEffect(() => {
    fetchPermissions({ scope: 'teacher', locale: i18n.language });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useMemo(() => {
    const teacherPermissionIds: number[] = idsArray(teacherPermissions);
    handlePermissionChange(teacherPermissionIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherPermissions]);

  const checkPermissionStatus = (id: any) => {
    const permissionIds: number[] = [];
    teacherPermissions.map((item: any) => permissionIds.push(item.id));
    if (permissionIds.includes(id)) {
      return true;
    }
    return false;
  };

  const handleEditPermissions = (id: any) => {
    const newPermissions = editedPermissions;
    if (checkPermissionStatus(id)) {
      // if permission id is included in permissions (true), remove
      let i = 0;
      while (i < newPermissions.length) {
        if (newPermissions[i] === id) {
          newPermissions.splice(i, 1);
        } else {
          ++i;
        }
      }
      // console.log("Removed: ", newPermissions);
      handlePermissionChange(newPermissions);
      enableButton(false);
      // }
    } else if (!checkPermissionStatus(id)) {
      // if permission id is not included in permissions (false), add
      newPermissions.push(id);
      // console.log("Added: ", newPermissions);
      handlePermissionChange(newPermissions);
      enableButton(false);
    }
  };

  return (
    <RendererIf
      condition={canEdit}
      alternative={
        <PermissionsNoToggle
          teacherPermissions={teacherPermissions}
          checkPermission={checkPermissionStatus}
        />
      }
    >
      <PermissionsWithToggle
        handleEdit={handleEditPermissions}
        checkPermission={checkPermissionStatus}
      />
    </RendererIf>
  );
};

export default SpecialPermissions;
